import React, { forwardRef, useEffect, useState, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { Link } from 'gatsby'
import P from 'components/new/typography/p'
import { Wrapper, Status } from '@googlemaps/react-wrapper'
import { LoadingScreen } from 'components/loading-screen'
import Button from 'components/button/button'
import { useQuery } from '@tanstack/react-query'
import { useEffectOnce } from 'hooks/use-effect-once'
import { usePreferredLocation } from 'hooks/use-preferred-location'
import { isBrowser } from '@hutson/utils'
import days from 'utils/constants/days'
import parseLocationHours from 'utils/location-hours/parse-location-hours'
import isSameDay from 'utils/dates/is-same-day'
import { useMeasure, useScrolling } from 'react-use'
import isDefined from 'utils/is-defined'
import mergeRefs from 'utils/merge-refs'
import { OutboundLink } from 'gatsby-plugin-google-gtag'

const render = status => {
  if (status === Status.FAILURE) {
    return <div>Failed to render map...</div>
  }
  return <StyledLoadingScreen loading={true} message='Loading map...' />
}

const MapContainer = props => {
  const { children } = props
  return (
    <div className='map-container'>
      <div className='map-container-inner'>
        <Wrapper apiKey={process.env.GATSBY_GOOGLE_API_KEY} render={render}>
          {children}
        </Wrapper>
      </div>
    </div>
  )
}

const Map = props => {
  const { locations, scrollToLocationResultByNumber, showingLocationCoordinates } = props

  /**
   * @type {React.MutableRefObject<google.maps.Map>}
   */
  const map = useRef(null)

  /**
   * @type {React.MutableRefObject<HTMLDivElement>}
   */
  const mapContainerEl = useRef(null)

  /**
   * @type {React.MutableRefObject<google.maps.Marker[]>}
   */
  const mapMarkers = useRef(null)

  useEffect(() => {
    if (map.current && showingLocationCoordinates) {
      map.current.setZoom(9)
      map.current.panTo(showingLocationCoordinates)
    }
  }, [showingLocationCoordinates])

  useEffectOnce(() => {
    if (isBrowser) {
      const newMap = new window.google.maps.Map(mapContainerEl.current, {
        zoom: 12,
        center: {
          lat: 36.7256143,
          lng: -88.6635187,
        },
      })

      /**
       * @type {google.maps.Marker[]}
       */
      const markers = locations.map(location => {
        return new window.google.maps.Marker({
          position: location.geolocation,
          title: location.title ? location.title : null,
          map: newMap,
        })
      })

      if (markers.length > 1) {
        const bounds = new window.google.maps.LatLngBounds()

        for (let i = 0; i < markers.length; i++) {
          bounds.extend(markers[i].position)

          markers[i].addListener('click', () => {
            scrollToLocationResultByNumber(String(locations[i].locationNumber))

            newMap.setZoom(9)
            newMap.panTo(locations[i].geolocation)
          })
        }

        newMap.fitBounds(bounds)
      } else {
        newMap.setCenter(markers[0].position)
      }

      mapMarkers.current = markers
      map.current = newMap
    }
  })

  return <div ref={mapContainerEl} id='map' />
}

const ResultsList = forwardRef((props, resultsListRef) => {
  const { children, dataUpdatedAt, setShowingLocationCoordinates } = props

  const resultsListScrolling = useScrolling(resultsListRef)

  const [resultsListSizeRef, resultsListSize] = useMeasure()

  const [resultsListBottomPadding, setResultsListBottomPadding] = useState(0)

  // Pan to the location marker for the location at the top of the sidebar
  useEffect(() => {
    if (!resultsListScrolling) {
      const topLocation = Array.prototype.slice
        .call(resultsListRef.current.childNodes)
        .find(
          node =>
            node.offsetTop <= resultsListRef.current.scrollTop &&
            node.offsetTop + node.offsetHeight > resultsListRef.current.scrollTop
        )
      if (topLocation) {
        setShowingLocationCoordinates({
          lat: Number(topLocation.getAttribute('data-geo-lat')),
          lng: Number(topLocation.getAttribute('data-geo-lng')),
        })
      }
    }
  }, [resultsListScrolling, resultsListRef, setShowingLocationCoordinates])

  useEffect(() => {
    resultsListRef.current.scrollTo({ top: 0, behavior: 'smooth' })
  }, [dataUpdatedAt, resultsListRef])

  // Apply padding to the bottom of the results list to allow for overflow scrolling
  useEffect(() => {
    const padding =
      resultsListRef.current.offsetHeight - resultsListRef.current.lastChild.offsetHeight

    setResultsListBottomPadding(padding < 0 ? 0 : padding)
  }, [dataUpdatedAt, resultsListSize.height, resultsListRef])
  return (
    <div
      className='results-list'
      ref={mergeRefs([resultsListRef, resultsListSizeRef])}
      style={{ paddingBottom: resultsListBottomPadding }}
    >
      {children}
    </div>
  )
})

const Result = props => {
  const {
    address,
    city,
    directionsLink,
    displayHours,
    displayStatus,
    distance,
    geolocation,
    locationNumber,
    phoneNumber,
    preferredLocation,
    setPreferredLocation,
    hydrated,
    slug,
    specialHoursTitle,
    state,
    title,
    zip,
  } = props

  const isPreferredLocation =
    preferredLocation && preferredLocation.locationNumber === locationNumber
  return (
    <div
      className='result'
      data-geo-lng={geolocation.lng}
      data-geo-lat={geolocation.lat}
      data-location-number={locationNumber}
    >
      <p className='location-title'>{title}</p>
      <div className='location-details'>
        <div className='location-details-col-1'>
          <p>
            <OutboundLink href={directionsLink} className='location-address'>
              {address}
              <br />
              {city}, {state} {zip}
            </OutboundLink>
          </p>
          <p>
            <OutboundLink href={`tel:${phoneNumber}`} className='location-phone'>
              {phoneNumber}
            </OutboundLink>
          </p>
        </div>
        <div className='location-details-col-2'>
          {/* Remount after hydration — the build-time HTML bakes in a stale open/closed
              status and React doesn't patch attribute mismatches (className) on hydration */}
          <p
            key={hydrated ? 'client' : 'server'}
            className={`location-open-status ${displayStatus === 'Open' ? 'open' : 'closed'}`}
          >
            {displayStatus}
          </p>
          {displayHours ? <p className='location-open-hours'>{displayHours}</p> : null}
          {specialHoursTitle ? (
            <p className='location-special-hours-title'>({specialHoursTitle})</p>
          ) : null}
          {isDefined(distance) ? <p className='location-distance'>{distance} miles away</p> : null}
        </div>
      </div>
      <div className='location-buttons'>
        <Button as={Link} to={slug} color='gray' size='small'>
          View store details
        </Button>
        <Button
          onClick={() => setPreferredLocation(locationNumber)}
          disabled={isPreferredLocation}
          color='yellow'
          size='small'
        >
          {isPreferredLocation ? 'My Store' : 'Set as My Store'}
        </Button>
      </div>
    </div>
  )
}

const SidebarContainer = forwardRef((props, resultsListRef) => {
  const { locations, setShowingLocationCoordinates } = props

  const locationNumbers = locations.map(location => location.locationNumber)
  locationNumbers.sort()

  const { preferredLocation, setPreferredLocation } = usePreferredLocation()

  const [hydrated, setHydrated] = useState(false)

  useEffectOnce(() => {
    setHydrated(true)
  })

  const [zip, setZip] = useState('')

  /**
   * @type {React.ChangeEventHandler<HTMLInputElement>}
   */
  const handleZipCodeChange = e => {
    setZip(e.target.value)
  }

  /**
   * @type {import('@tanstack/react-query').UseQueryResult<import('../api/nearby-locations').NearbyLocation[]>}
   */
  const { isFetching, isError, isSuccess, data, dataUpdatedAt } = useQuery({
    queryKey: ['nearby-locations', { zip, locationNumbers }],
    staleTime: 1000 * 60 * 15, // 15 minutes
    queryFn: async () => {
      /**
       * @type {import('../api/nearby-locations').NearbyLocationsHandlerResponse}
       */
      const response = await fetch('/api/nearby-locations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          distancesOnly: true,
          locationNumbers,
          zip,
        }),
      }).then(res => {
        if (!res.ok) {
          throw new Error('Network response was not okay')
        }
        return res.json()
      })

      if (response.error) {
        throw new Error(response.error)
      }

      return response.result
    },
    keepPreviousData: true,
    enabled: zip.length === 5,
  })

  const todayDate = new Date()

  const currentDayOfWeek = days[new Date().getDay()]

  const nearbyLocations = locations.map(location => {
    const todaySpecialHours = Array.isArray(location.specialHours)
      ? location.specialHours.find(specialHours =>
          isSameDay(todayDate, new Date(specialHours.date))
        )
      : null

    const specialHoursTitle = todaySpecialHours ? todaySpecialHours.title : null

    const todayHours = todaySpecialHours
      ? todaySpecialHours.hours
      : location.hours[currentDayOfWeek]

    const todayHoursParsed = parseLocationHours(todayHours)

    const startDate =
      todayHoursParsed.closed === false
        ? new Date(
            todayDate.getFullYear(),
            todayDate.getMonth(),
            todayDate.getDate(),
            todayHoursParsed.start.slice(0, 2),
            todayHoursParsed.start.slice(3)
          )
        : null

    const endDate =
      todayHoursParsed.closed === false
        ? new Date(
            todayDate.getFullYear(),
            todayDate.getMonth(),
            todayDate.getDate(),
            todayHoursParsed.end.slice(0, 2),
            todayHoursParsed.end.slice(3)
          )
        : null

    // Parse hours, check if currently open
    const currentlyOpen = startDate < todayDate && endDate > todayDate

    const displayStatus =
      location.status === 'permanently-closed'
        ? 'Permanently Closed'
        : location.status === 'temporarily-closed'
        ? 'Temporarily Closed'
        : currentlyOpen
        ? 'Open'
        : 'Closed'

    const displayHours =
      location.status === 'temporarily-closed' || todayHoursParsed.closed ? null : todayHours

    const distancesMatch = Array.isArray(data)
      ? data.find(result => result.locationNumber === location.locationNumber)
      : null

    const distance = distancesMatch ? distancesMatch.distance : null

    return {
      ...location,
      distance,
      currentlyOpen,
      displayStatus,
      displayHours,
      specialHoursTitle,
    }
  })

  nearbyLocations.sort((a, b) => {
    if (!isDefined(a.distance)) {
      return 1
    }

    if (!isDefined(b.distance)) {
      return -1
    }

    return Number(a.distance) - Number(b.distance)
  })

  return (
    <div className='sidebar'>
      <div className='search-container'>
        <P>Find your nearest location</P>
        <label htmlFor='location-search-zip' hidden>
          Zip code
        </label>
        <div className='location-search-input-container'>
          {isFetching ? (
            <div className='loading-container'>
              <div className='loading-circle'></div>
            </div>
          ) : null}
          <input
            id='location-search-zip'
            name='location-search-zip'
            type='text'
            inputMode='numberic'
            placeholder='Search by zip code'
            pattern='^\d{5}$'
            maxLength={5}
            onChange={handleZipCodeChange}
            value={zip}
          />
        </div>
      </div>
      <ResultsList
        ref={resultsListRef}
        setShowingLocationCoordinates={setShowingLocationCoordinates}
        dataUpdatedAt={dataUpdatedAt}
      >
        {isError || (isSuccess && (!Array.isArray(data) || data.length === 0)) ? (
          <div className='message-container'>
            <p>
              We ran into a problem while trying to search for nearby locations. If problems
              persist, please contact our support team at{' '}
              <OutboundLink href='contactus@hutsoninc.com'>contactus@hutsoninc.com</OutboundLink>.
            </p>
          </div>
        ) : null}
        {nearbyLocations.length > 0
          ? nearbyLocations.map(location => (
              <Result
                key={location.locationNumber}
                hydrated={hydrated}
                preferredLocation={preferredLocation}
                setPreferredLocation={setPreferredLocation}
                {...location}
              />
            ))
          : null}
      </ResultsList>
    </div>
  )
})

const LocationsMapSearch = props => {
  const { locations, ...rest } = props

  /**
   * @type {[google.maps.LatLngLiteral, React.Dispatch<React.SetStateAction<google.maps.LatLngLiteral>>]}
   */
  const [showingLocationCoordinates, setShowingLocationCoordinates] = useState(null)

  /**
   * @type {React.MutableRefObject<HTMLDivElement>}
   */
  const resultsListRef = useRef(null)

  /**
   * @param {string} locationNumber
   */
  const scrollToLocationResultByNumber = locationNumber => {
    if (resultsListRef.current) {
      const locationResultMatch = Array.prototype.slice
        .call(resultsListRef.current.childNodes)
        .find(node => node.getAttribute('data-location-number') === locationNumber)
      if (locationResultMatch) {
        resultsListRef.current.scrollTo({ top: locationResultMatch.offsetTop, behavior: 'smooth' })
      }
    }
  }

  return (
    <Container {...rest}>
      <div className='container-inner'>
        <MapContainer>
          <Map
            locations={locations}
            showingLocationCoordinates={showingLocationCoordinates}
            scrollToLocationResultByNumber={scrollToLocationResultByNumber}
          />
        </MapContainer>
        <SidebarContainer
          ref={resultsListRef}
          locations={locations}
          setShowingLocationCoordinates={setShowingLocationCoordinates}
        />
      </div>
    </Container>
  )
}

const spinAnimation = keyframes`
  100% {
    transform: rotate(360deg);
  }
`

const StyledLoadingScreen = styled(LoadingScreen)`
  background-color: ${props => props.theme.color.n30};
  height: 100%;
  width: 100%;
`

const Container = styled.div`
  border-bottom: 1px solid ${props => props.theme.color.n30};
  border-top: 1px solid ${props => props.theme.color.n30};

  .container-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  }

  #map {
    height: 100%;
    width: 100%;
  }

  .sidebar {
    background-color: ${props => props.theme.color.n10};
    align-items: flex-start;
    display: flex;
    flex-direction: column;

    .search-container {
      background-color: ${props => props.theme.color.g400};
      padding: 20px 16px;
      width: 100%;

      p {
        color: #fff;
        font-weight: 600;
        line-height: 1.3;
        margin: 0;
      }
    }

    .location-search-input-container {
      position: relative;
    }

    .loading-container {
      padding: 8px;
      position: absolute;
      right: 0;
      top: 0;
    }

    .loading-circle {
      animation: ${spinAnimation} 0.67s infinite linear;
      border: 4px solid ${props => props.theme.color.n20};
      border-radius: 100%;
      border-top-color: ${props => props.theme.color.g400};
      box-sizing: border-box;
      height: 24px;
      width: 24px;
    }

    label {
      font-weight: bold;
    }

    input {
      flex-shrink: 0;
      width: 100%;
    }

    .results-list {
      flex-grow: 2;
      overflow-y: auto;
      position: relative;
      width: 100%;
    }

    .result {
      background-color: #fff;
      border-top: 1px solid ${props => props.theme.color.n30};
      padding: 16px;

      :last-child {
        border-bottom: 1px solid ${props => props.theme.color.n30};
      }

      p {
        color: ${props => props.theme.color.n900};
        font-size: 16px;
        line-height: 1.3;
        margin: 0;
      }

      .location-title {
        font-size: 18px;
        font-weight: 600;
        margin: 0 0 8px;
      }

      .location-details {
        display: flex;
        flex-direction: row;
      }

      .location-details-col-1 {
        flex-grow: 2;
      }

      .location-address {
        color: ${props => props.theme.color.n900};
        display: inline-block;
        margin: 0 0 8px;
        text-decoration: none;

        :hover,
        :focus {
          text-decoration: underline;
        }
      }

      .location-phone {
        color: ${props => props.theme.color.n900};
        display: inline-block;
        margin: 0;
        text-decoration: none;

        :hover,
        :focus {
          text-decoration: underline;
        }
      }

      .location-details-col-2 {
        text-align: right;
      }

      .location-open-status {
        font-size: 14px;
        font-weight: 700;
        margin: 0;

        &.open {
          color: ${props => props.theme.color.g400};
        }

        &.closed {
          color: ${props => props.theme.color.r500};
        }
      }

      .location-open-hours {
        margin: 4px 0 0;
      }

      .location-special-hours-title {
        color: ${props => props.theme.color.n400};
        font-size: 14px;
        margin: 0;
      }

      .location-distance {
        margin: 8px 0 0;
      }

      .location-buttons {
        margin-top: 16px;

        button,
        a {
          display: inline-block;
          margin-bottom: 8px;
          text-decoration: none;

          :not(:last-child) {
            margin-right: 8px;
          }
        }
      }
    }

    .message-container {
      background-color: ${props => props.theme.color.n40};
      padding: 16px;

      p {
        margin: 0;
      }

      a {
        color: ${props => props.theme.color.g400};
        text-decoration: underline;

        :hover,
        :focus {
          text-decoration: none;
        }
      }
    }
  }

  @media (max-width: 899px) {
    .map-container {
      display: block;
      height: 0px;
      overflow: hidden;
      padding-bottom: 56.25%;
      position: relative;
      width: auto;
    }

    .map-container-inner {
      height: 100%;
      position: absolute;
      width: 100%;
    }

    .sidebar {
      max-height: 350px;
    }
  }

  @media (min-width: 900px) {
    display: block;
    height: 0px;
    overflow: hidden;
    padding-bottom: 43.75%;
    position: relative;
    width: auto;

    .container-inner {
      display: flex;
      flex-direction: row-reverse;
      position: absolute;
    }

    .map-container {
      height: 100%;
      width: 65%;
    }

    .map-container-inner {
      height: 100%;
    }

    .sidebar {
      height: 100%;
      position: relative;
      width: 35%;
    }
  }
`

export default LocationsMapSearch
